import { useCallback } from 'react'

import { useSearchParams } from 'react-router-dom'

import {
  ContentModule,
  ContentModuleType,
  PostType,
} from '@tribeplatform/gql-client/types'

import {
  CONTENT_MODULE_ID_QUERY_PARAM,
  RETURN_TO_QUERY_PARAM,
  CONTENT_MODULE_TYPE_QUERY_PARAM,
  POST_TYPE_QUERY_PARAM,
} from '../../common/constants/content-module.js'

interface LinkToContentModalParams {
  spaceRelativeUrl: string
  contentId: string
  contentType: ContentModuleType
}

interface LinkToContentCreateFormParams {
  contentType: PostType | ContentModule | ContentModuleType
  spaceRelativeUrl?: string
  returnTo?: string
}

interface LinkToContentEditFormParams {
  relativeUrl: string
  returnTo?: string
}

export const useContentModuleUtils = () => {
  const searchParams = useSearchParams()

  const linkToContentModal = useCallback(
    ({
      spaceRelativeUrl,
      contentId,
      contentType,
    }: LinkToContentModalParams) => {
      const params = new URLSearchParams(searchParams.toString())
      params.set(CONTENT_MODULE_TYPE_QUERY_PARAM, contentType)
      params.set(CONTENT_MODULE_ID_QUERY_PARAM, contentId)
      return `${spaceRelativeUrl}?${params}`
    },
    [searchParams],
  )

  const isPostType = useCallback(
    (
      content: PostType | ContentModule | ContentModuleType,
    ): content is PostType => {
      return (
        typeof content === 'object' &&
        content !== null &&
        'id' in content &&
        'slug' in content
      )
    },
    [],
  )

  const isContentModule = useCallback(
    (
      content: PostType | ContentModule | ContentModuleType,
    ): content is ContentModule => {
      return (
        typeof content === 'object' &&
        content !== null &&
        'contentModuleType' in content
      )
    },
    [],
  )

  const isContentModuleType = useCallback(
    (
      content: PostType | ContentModule | ContentModuleType,
    ): content is ContentModuleType => {
      return !isPostType(content) && !isContentModule(content)
    },
    [isContentModule, isPostType],
  )

  const linkToNewContentForm = useCallback(
    ({
      spaceRelativeUrl = '',
      contentType,
      returnTo,
    }: LinkToContentCreateFormParams) => {
      const params = new URLSearchParams()

      if (isPostType(contentType)) {
        params.set(POST_TYPE_QUERY_PARAM, contentType.id)
      } else if (isContentModuleType(contentType)) {
        params.set(CONTENT_MODULE_TYPE_QUERY_PARAM, contentType)
      } else {
        params.set(
          CONTENT_MODULE_TYPE_QUERY_PARAM,
          contentType.contentModuleType,
        )
      }

      if (returnTo) {
        params.set(RETURN_TO_QUERY_PARAM, returnTo)
      }

      return `${spaceRelativeUrl}/new?${params}`
    },
    [isPostType, isContentModuleType],
  )

  const linkToEditContentForm = useCallback(
    ({ relativeUrl, returnTo }: LinkToContentEditFormParams) => {
      const params = new URLSearchParams()
      if (returnTo) {
        params.set(RETURN_TO_QUERY_PARAM, returnTo)
      }
      return `${relativeUrl}/edit?${params}`
    },
    [],
  )

  return {
    linkToContentModal,
    linkToNewContentForm,
    linkToEditContentForm,
    isPostType,
    isContentModule,
    isContentModuleType,
  }
}
